/**
 * downloadFile — force a real "Save As" download even when the resource is on a
 * different origin (e.g. Cloudflare R2 pub-* URLs).
 *
 * Why this exists: the HTML `<a href download>` attribute is silently IGNORED
 * by browsers when the file is cross-origin. The standard workaround is to
 * fetch the file as a Blob, create an object URL, and click a temporary link.
 *
 * Requires the bucket to allow GET from this origin (R2 CORS is already set
 * up — see /app/backend/studio.py R2_PUBLIC_URL).
 *
 * If the fetch fails (CORS, network), we gracefully fall back to opening
 * the resource in a new tab.
 */
export async function downloadFile(url, filename) {
  if (!url) return;
  try {
    const res = await fetch(url, { mode: "cors", credentials: "omit" });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = filename || extractFilename(url) || "download";
    a.rel = "noopener noreferrer";
    document.body.appendChild(a);
    a.click();
    a.remove();
    // Free the object URL on the next tick (some browsers need the click to register first).
    setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
  } catch (e) {
    // Fallback: open in a new tab (user can right-click → Save As).
    console.warn("downloadFile fallback (likely CORS):", e);
    window.open(url, "_blank", "noopener,noreferrer");
  }
}

function extractFilename(url) {
  try {
    const u = new URL(url);
    const parts = u.pathname.split("/").filter(Boolean);
    return parts[parts.length - 1] || null;
  } catch {
    return null;
  }
}
